import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaPlay, FaPause, FaCalendarAlt, FaHeart, FaSyncAlt, FaExpand, FaCompress } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { useParams } from 'react-router-dom';
import { getProfile } from '../../../services/api';

export default function NewYearSlide({
  showSlideView,
  slideshowWishes,
  currentIndex,
  setCurrentIndex,
  isAutoSlideEnabled,
  toggleAutoSlide,
  closeSlideView,
  countdown,
  formatDate,
  getInitials,
  loadWishes,
  refreshInterval = 200, // Geri sayım süresi (saniye cinsinden, varsayılan 60)
}) {
  const [isLandscape, setIsLandscape] = useState(false);
  const [rotationAngle, setRotationAngle] = useState(0); // 0, 90, 270 derece
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  const [refreshCountdown, setRefreshCountdown] = useState(refreshInterval);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const { id } = useParams();
  const currentPageUrl = id ? `${window.location.origin}/${id}` : window.location.href;

  // Ekran yönünü, genişliğini ve yüksekliğini kontrol et
  useEffect(() => {
    const checkOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
      setScreenWidth(window.innerWidth);
      setScreenHeight(window.innerHeight);
    };

    checkOrientation();
    window.addEventListener('resize', checkOrientation);

    return () => window.removeEventListener('resize', checkOrientation);
  }, []);

  // Scale değerini ekran genişliğine göre hesapla (1920px için scale 2)
  const calculateScale = () => {
    const referenceWidth = 1920;
    const referenceScale = 2;
    const calculatedScale = (screenWidth / referenceWidth) * referenceScale;
    // Minimum scale değeri 1.5, maksimum 3 olarak sınırla
    return Math.max(1.5, Math.min(3, calculatedScale));
  };

  // Fullscreen durumunu kontrol et
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreenActive = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isFullscreenActive);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // 60 saniyelik geri sayım
  useEffect(() => {
    if (!showSlideView) return;

    const interval = setInterval(() => {
      setRefreshCountdown((prev) => {
        if (prev <= 1) {
          // Geri sayım doldu, refresh flag'ini set et
          setShouldRefresh(true);
          return refreshInterval; // Geri sayımı sıfırla
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showSlideView, refreshInterval]);

  // Dilekleri yenile (ayrı useEffect ile)
  useEffect(() => {
    if (shouldRefresh && loadWishes) {
      loadWishes();
      setShouldRefresh(false);
    }
  }, [shouldRefresh, loadWishes]);

  // Slide görünümü açıldığında geri sayımı sıfırla
  useEffect(() => {
    if (showSlideView) {
      setRefreshCountdown(refreshInterval);
    }
  }, [showSlideView, refreshInterval]);

  // Profile bilgilerini al
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getProfile();
        // API response.user veya direkt response döndürebilir
        const userData = response?.user || response;
        if (userData) {
          setProfileData(userData);
        }
      } catch (error) {
        console.error('Profile bilgileri alınamadı:', error);
      }
    };

    if (showSlideView) {
      fetchProfile();
    }
  }, [showSlideView]);

  // currentIndex'i slideshowWishes array'inin boyutuna göre kontrol et
  useEffect(() => {
    if (slideshowWishes.length > 0 && currentIndex >= slideshowWishes.length) {
      setCurrentIndex(0);
    }
  }, [slideshowWishes.length, currentIndex, setCurrentIndex]);

  const handleRotate = () => {
    // 0° → 90° (en üst sağda) → 270° (en üst solda - başlangıç soldan) → 0° döngüsü
    setRotationAngle((prev) => {
      if (prev === 0) return 90;
      if (prev === 90) return 270; // Tam zıttı - görselin başlangıcı soldan
      return 0; // 270'den sonra 0'a dön
    });
  };

  const handleFullscreen = async () => {
    try {
      if (isFullscreen) {
        // Tam ekrandan çık
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          await document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      } else {
        // Tam ekrana geç
        const element = document.documentElement;
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
          await element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen();
        }
      }
    } catch (error) {
      console.error('Fullscreen hatası:', error);
    }
  };

  return (
    <AnimatePresence>
      {showSlideView && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] bg-black/90 overflow-hidden"
        >
          {/* Arkaplan Görseli */}
          <img
            src="/assets/newyearjodiac.jpg"
            alt="Background"
            className={`absolute inset-0 w-full h-full transition-transform duration-500 ease-in-out ${
              rotationAngle !== 0 ? 'object-contain' : 'object-cover'
            }`}
            style={{
              transform: rotationAngle !== 0
                ? `rotate(${rotationAngle}deg) scale(${calculateScale()})` 
                : 'rotate(0deg) scale(1)',
              transformOrigin: 'center center',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30" />
          
          {/* Geri Butonu */}
          <button
            onClick={closeSlideView}
            className="absolute top-4 left-4 sm:top-6 sm:left-6 z-50 p-2.5 sm:p-3 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/30 transition-all duration-300 hover:scale-110 active:scale-95 shadow-lg"
            title="Geri Dön"
          >
            <FaArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          {/* Otomatik Kaydırma Switch - Sağ Üst - Sadece mesaj varsa göster */}
          {slideshowWishes.length > 0 && (
            <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-50 flex items-center gap-2 sm:gap-3">
              <span className={`text-white text-xs sm:text-sm font-medium ${isAutoSlideEnabled ? 'opacity-100' : 'opacity-70'}`}>
                {isAutoSlideEnabled ? <FaPause className="w-3 h-3 sm:w-4 sm:h-4" /> : <FaPlay className="w-3 h-3 sm:w-4 sm:h-4" />}
              </span>
              <button
                onClick={toggleAutoSlide}
                className={`relative inline-flex h-6 w-12 sm:h-7 sm:w-14 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent cursor-pointer ${
                  isAutoSlideEnabled
                    ? 'bg-white/30 backdrop-blur-md'
                    : 'bg-white/20 backdrop-blur-md'
                }`}
                title={isAutoSlideEnabled ? 'Otomatik kaydırmayı durdur' : 'Otomatik kaydırmayı başlat'}
              >
                <span
                  className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
                    isAutoSlideEnabled ? 'translate-x-7 sm:translate-x-8' : 'translate-x-1'
                  }`}
                />
              </button>
              
              {/* Döndürme İkonu - Sadece landscape (TV/monitör) ekranlarda göster */}
              {isLandscape && (
                <button
                  onClick={handleRotate}
                  className={`p-2 sm:p-2.5 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/30 transition-all duration-300 hover:scale-110 active:scale-95 shadow-lg ${
                    rotationAngle !== 0 ? 'bg-white/30' : ''
                  }`}
                  title={rotationAngle === 0 ? 'Arkaplanı döndür' : rotationAngle === 90 ? 'Tam zıttına döndür' : 'Eski haline dön'}
                >
                  <FaSyncAlt className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 ${rotationAngle === 270 ? 'rotate-180' : ''}`} />
                </button>
              )}

              {/* Fullscreen İkonu - Sadece landscape (TV/monitör) ekranlarda göster */}
              {isLandscape && (
                <button
                  onClick={handleFullscreen}
                  className={`p-2 sm:p-2.5 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/30 transition-all duration-300 hover:scale-110 active:scale-95 shadow-lg ${
                    isFullscreen ? 'bg-white/30' : ''
                  }`}
                  title={isFullscreen ? 'Tam ekrandan çık' : 'Tam ekrana geç'}
                >
                  {isFullscreen ? (
                    <FaCompress className="w-3 h-3 sm:w-4 sm:h-4" />
                  ) : (
                    <FaExpand className="w-3 h-3 sm:w-4 sm:h-4" />
                  )}
                </button>
              )}

              {/* Geri Sayım - Dilek Yenileme */}
              {isLandscape && loadWishes && (
                <div 
                  className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/20 backdrop-blur-md rounded-full text-white"
                  style={{
                    transform: rotationAngle !== 0 ? `rotate(${rotationAngle}deg)` : 'rotate(0deg)',
                    marginTop: rotationAngle !== 0 ?'30px' : '0px',
                    transition: 'transform 0.5s ease-in-out',
                  }}
                >
                  <span className="text-xs sm:text-sm font-medium">Yenileme:</span>
                  <span className="text-xs sm:text-sm font-bold tabular-nums">{refreshCountdown}s</span>
                </div>
              )}
            </div>
          )}

          {/* Slide İçeriği */}
          <div 
            className="relative w-full h-full flex flex-col items-center justify-end px-4 sm:px-6 pb-[100px] pt-4 sm:pt-6 transition-transform duration-1000 ease-in-out"
            style={{
              transform: rotationAngle !== 0
                ? `rotate(${rotationAngle}deg) translateY(${screenHeight * 0.125}px)` 
                : 'rotate(0deg) translateY(0px)',
            }}
          >
            {/* Üst Kartlar - Jodiac Logo, QR Kod, Profile Logo */}
            <div className="flex gap-4 sm:gap-6 mb-4 sm:mb-6 z-40 flex-shrink-0">
              {/* Sol Kart - Jodiac Logo */}
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-xl flex items-center justify-center p-2 sm:p-3">
                <img 
                  src="/logo512.png" 
                  alt="Jodiac Logo" 
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Orta Kart - QR Kod */}
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-xl flex items-center justify-center p-2 sm:p-3">
                <QRCodeSVG 
                  value={currentPageUrl}
                  size={64}
                  level="H"
                  includeMargin={false}
                  className="w-full h-full"
                />
              </div>

              {/* Sağ Kart - Profile Logo */}
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-xl flex items-center justify-center p-2 sm:p-3 overflow-hidden">
                {profileData?.profile_picture_url ? (
                  <img 
                    src={profileData.profile_picture_url} 
                    alt="Profile" 
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : profileData?.pictureDetails?.pictureUrl || profileData?.pictureDetails?.url ? (
                  <img 
                    src={profileData.pictureDetails.pictureUrl || profileData.pictureDetails.url} 
                    alt="Profile" 
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : profileData ? (
                  <div className="w-full h-full rounded-lg bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8] flex items-center justify-center">
                    <span className="text-white font-bold text-lg sm:text-xl md:text-2xl">
                      {profileData?.first_name?.charAt(0)?.toUpperCase()}
                      {profileData?.last_name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                ) : (
                  <div className="w-full h-full rounded-lg bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400 text-xs">Yükleniyor...</span>
                  </div>
                )}
              </div>
            </div>
            <div className="relative w-full max-w-lg sm:max-w-xl md:max-w-2xl">
              {/* Eğer gösterilecek mesaj yoksa Geri Sayım Ekranı (sadece normal kullanıcılar için) */}
              {slideshowWishes.length === 0 ? (
                <motion.div
                  initial={{ y: 100, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6 }}
                  className="flex flex-col items-center justify-end text-center"
                >
                  {/* Geri Sayım */}
                  <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                      Yeni Yıla
                    </h2>
                    <p className="text-gray-600 text-sm sm:text-base mb-4 sm:mb-6">Kalan Süre</p>
                    <div className="grid grid-cols-4 gap-2 sm:gap-3">
                      <div className="flex flex-col items-center">
                        <div className="bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8] text-white rounded-xl p-2 sm:p-3 w-14 sm:w-16 h-14 sm:h-16 flex items-center justify-center shadow-lg">
                          <span className="text-xl sm:text-2xl font-bold">{countdown.days}</span>
                        </div>
                        <span className="text-gray-700 text-xs sm:text-sm mt-2 font-semibold">Gün</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8] text-white rounded-xl p-2 sm:p-3 w-14 sm:w-16 h-14 sm:h-16 flex items-center justify-center shadow-lg">
                          <span className="text-xl sm:text-2xl font-bold">{countdown.hours}</span>
                        </div>
                        <span className="text-gray-700 text-xs sm:text-sm mt-2 font-semibold">Saat</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8] text-white rounded-xl p-2 sm:p-3 w-14 sm:w-16 h-14 sm:h-16 flex items-center justify-center shadow-lg">
                          <span className="text-xl sm:text-2xl font-bold">{countdown.minutes}</span>
                        </div>
                        <span className="text-gray-700 text-xs sm:text-sm mt-2 font-semibold">Dakika</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8] text-white rounded-xl p-2 sm:p-3 w-14 sm:w-16 h-14 sm:h-16 flex items-center justify-center shadow-lg">
                          <span className="text-xl sm:text-2xl font-bold">{countdown.seconds}</span>
                        </div>
                        <span className="text-gray-700 text-xs sm:text-sm mt-2 font-semibold">Saniye</span>
                      </div>
                    </div>
                  </div>

                  {/* Website URL */}
                  <a 
                    href="https://www.jodi.ac" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white text-base sm:text-lg font-semibold hover:text-white/80 transition-colors bg-white/10 backdrop-blur-sm px-5 py-2.5 rounded-full"
                  >
                    www.jodi.ac
                  </a>
                </motion.div>
              ) : (
                slideshowWishes[currentIndex] && (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentIndex}
                      initial={{ y: 200, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -200, opacity: 0 }}
                      transition={{ 
                        duration: 0.6,
                        ease: "easeInOut"
                      }}
                    >
                      <div
                        className={`bg-white/95 backdrop-blur-sm border-2 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl ${
                          slideshowWishes[currentIndex]?.request_status === 'accepted'
                            ? 'border-white/30'
                            : 'border-white/50 bg-gradient-to-br from-white/95 to-white/90'
                        }`}
                      >
                    <div className="flex items-start gap-3 sm:gap-4 md:gap-6">
                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        {(() => {
                          const wish = slideshowWishes[currentIndex];
                          const senderRole = wish?.sender_role;
                          const isAdmin = senderRole === 'ADMIN';
                          const isUser = senderRole === 'USER';
                          
                          return (
                            <div
                              className={`w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base md:text-xl shadow-xl ${
                                wish?.request_status === 'accepted'
                                  ? 'bg-gradient-to-br from-gray-400 to-gray-500'
                                  : 'bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8]'
                              } ${isAdmin ? 'border-2 border-red-500' : isUser ? 'border-2 border-[#39a4b4]' : ''}`}
                            >
                              {getInitials(wish?.sender_name || '')}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {(() => {
                          const wish = slideshowWishes[currentIndex];
                          const senderRole = wish?.sender_role;
                          const isAdmin = senderRole === 'ADMIN';
                          const isUser = senderRole === 'USER';
                          
                          return (
                            <div className="flex items-start justify-between gap-2 sm:gap-3 md:gap-4 mb-2 sm:mb-3 md:mb-4">
                              <div className="flex-1 min-w-0 text-left">
                                <h3 className="text-gray-800 font-bold text-lg sm:text-xl md:text-2xl mb-1 sm:mb-2 md:mb-3 text-left">
                                  {wish?.sender_name || ''}
                                </h3>
                                {wish?.is_jodiac_user === 1 && !isAdmin && !isUser && (
                                  <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-[#5abcc8] text-white mb-2">
                                    Jodiac Kullanıcısı
                                  </span>
                                )}
                                <div className="flex items-center gap-1.5 sm:gap-2 text-gray-500 text-xs sm:text-sm md:text-base justify-start">
                                  <FaCalendarAlt className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 flex-shrink-0" />
                                  <span className="truncate text-left">{formatDate(wish?.created_at || '')}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {isAdmin && (
                                  <span className="inline-block px-2 py-0.5 rounded-full text-xs sm:text-sm font-medium bg-red-500 text-white whitespace-nowrap">
                                    Jodiac Kurucu
                                  </span>
                                )}
                                {isUser && (
                                  <span className="inline-block px-2 py-0.5 rounded-full text-xs sm:text-sm font-medium bg-[#39a4b4] text-white whitespace-nowrap">
                                    Jodiac Üyesi
                                  </span>
                                )}
                                {wish?.request_status === 'pending' && (
                                  <div className="flex-shrink-0">
                                    <span className="bg-[#3DA7B5] text-white text-xs sm:text-sm px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-full font-medium shadow-lg">
                                      Yeni
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        <div className="mt-3 sm:mt-4 md:mt-6">
                          <h4 className="text-gray-800 font-semibold text-base sm:text-lg md:text-xl mb-2 text-left">
                            {slideshowWishes[currentIndex]?.title || ''}
                          </h4>

                          {/* Fotoğraf */}
                          {slideshowWishes[currentIndex]?.photo_url && (
                            <div className="mb-4">
                              <img 
                                src={slideshowWishes[currentIndex]?.photo_url} 
                                alt={slideshowWishes[currentIndex]?.title || ''}
                                className="w-full h-48 sm:h-56 md:h-64 object-cover rounded-xl shadow-lg"
                              />
                            </div>
                          )}

                          {/* İçerik */}
                          {slideshowWishes[currentIndex]?.content && (
                            <p className="text-gray-700 text-sm sm:text-base md:text-lg leading-relaxed text-left">
                              {slideshowWishes[currentIndex]?.content}
                            </p>
                          )}
                        </div>

                        {/* Decorative Elements */}
                        <div className="flex items-center justify-between mt-4 sm:mt-6 md:mt-8 pt-3 sm:pt-4 md:pt-6 border-t border-gray-200">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <FaHeart className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-red-400 flex-shrink-0" />
                            <span className="text-gray-500 text-xs sm:text-sm md:text-base font-medium">Yılbaşı Dileği</span>
                          </div>
                          {slideshowWishes[currentIndex]?.social_media && (
                            <span className="text-gray-500 text-xs sm:text-sm md:text-base font-medium">
                              {slideshowWishes[currentIndex].social_media}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
                )
              )}

              {/* İndikatörler - Sadece mesaj varsa göster */}
              {slideshowWishes.length > 0 && (
                <>
                  <div className="absolute -bottom-12 sm:-bottom-16 left-1/2 transform -translate-x-1/2 flex gap-2 sm:gap-3 z-20">
                    {slideshowWishes.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentIndex(index)}
                        className={`h-2 sm:h-2.5 md:h-3 rounded-full transition-all duration-300 ${
                          index === currentIndex
                            ? 'w-8 sm:w-9 md:w-10 bg-white shadow-lg'
                            : 'w-2 sm:w-2.5 md:w-3 bg-white/50 hover:bg-white/70'
                        }`}
                        aria-label={`Dilek ${index + 1}`}
                      />
                    ))}
                  </div>

                  {/* Bilgi Metni */}
                  <div className="absolute -bottom-20 sm:-bottom-24 md:-bottom-28 left-1/2 transform -translate-x-1/2 text-center z-20">
                    <p className="text-white/90 text-xs sm:text-sm font-medium backdrop-blur-sm bg-white/10 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                      {currentIndex + 1} / {slideshowWishes.length} dilek
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

